import { createRouter, createWebHistory } from "vue-router";
import HomeView from "@/views/HomeView.vue";
import LoginView from "@/views/login-view/LoginView.vue";
import LoginLinkSentView from "@/views/login-link-sent-view/LoginLinkSentView.vue";
import QuestionnaireView from "../views/questionnaire/QuestionnaireView.vue";
import QuestionnairesView from "@/views/questionnaires/QuestionnairesView.vue";
import DesignView from "../views/Design/DesignView.vue";
import SupplierRegistryView from "@/views/supplier-registry/SupplierRegistryView.vue";
import { useAuthenticationStore } from "@/stores/authentication";
import { RouterPath } from "@/models/enum/routerPath";
import { useRouterHelper } from "@/stores/routerHelper";
import QuestionnaireAnswersView from "@/views/reports/QuestionnaireAnswersView.vue";
import QuestionnaireTemplateView from "@/views/template/QuestionnaireTemplateView.vue";
import CustomerTermsAndConditions from "@/views/terms-view/CustomerTermsAndConditions.vue";
import adminRoutes from "@/router/adminRoutes";
import superUserRoutes from "@/router/superUserRoutes";
import TeamView from "@/views/users/TeamView.vue";
import DashboardView from "@/views/dashboard-view/DashboardView.vue";
import CreateQuestionnaireView from "@/views/questionnaires/CreateQuestionnaireView.vue";
import EditQuestionnaireView from "@/views/questionnaires/EditQuestionnaireView.vue";
import ManagerUserProfileView from "@/views/ManagerUserProfileView.vue";
import SendQuestionnaireView from "@/views/questionnaires/SendQuestionnaireView.vue";
import StartQuestionnaireView from "@/views/questionnaire/StartQuestionnaireView.vue";
import LoginHelpView from "@/views/login-help-view/LoginHelpView.vue";
import DetailedRiskPageView from "@/views/dashboard-view/DetailedRiskPageView.vue";
import UnitProfileView from "@/views/unit-profile/UnitProfileView.vue";
import ReceivedQuestionnaireStatus from "@/views/questionnaires/ReceivedQuestionnaireStatusView.vue";
import MyQuestionnaireStatus from "@/views/questionnaires/my-questionnaires/MyQuestionnaireStatusView.vue";
import registerNewSupplierRoutes, {
  allRegisterSupplierRoutes,
} from "@/router/registerSupplierRoutes";
import helpRoutes from "@/router/helpRoutes";
import UploadSuppliersView from "@/views/upload-suppliers/UploadSuppliersView.vue";
import UploadSuppliersQuestionnaireView from "@/views/questionnaire/UploadSuppliersQuestionnaireView.vue";
import ForwardQuestionnaireView from "@/views/questionnaire/ForwardQuestionnaireView.vue";
import ReuseAnswersView from "@/views/questionnaire/ReuseAnswersView.vue";
import LoggedOutView from "@/views/login-view/LoggedOutView.vue";
import LoginTokenExpiredView from "@/views/login-view/LoginTokenExpiredView.vue";
import VerifyLinkView from "@/views/login-view/VerifyLinkView.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: RouterPath.Home,
      name: "home",
      component: HomeView,
      meta: { disableBackButton: true },
    },
    {
      path: RouterPath.Login,
      name: RouterPath.Login,
      component: LoginView,
      meta: { open: true },
    },
    {
      path: RouterPath.LoggedOut,
      name: RouterPath.LoggedOut,
      component: LoggedOutView,
      meta: { open: true },
    },
    {
      path: RouterPath.LoginTokenExpired,
      name: RouterPath.LoginTokenExpired,
      component: LoginTokenExpiredView,
      meta: { open: true },
    },
    {
      path: RouterPath.LoginLinkSent,
      name: RouterPath.LoginLinkSent,
      component: LoginLinkSentView,
      meta: { open: true },
    },
    {
      path: RouterPath.VerifyLink,
      name: RouterPath.VerifyLink,
      component: VerifyLinkView,
      meta: { open: true },
    },
    {
      path: RouterPath.StartQuestionnaire,
      name: "start-questionnaire",
      component: StartQuestionnaireView,
    },
    {
      path: RouterPath.Questionnaire,
      name: "questionnaire",
      component: QuestionnaireView,
      meta: { disableBackButton: true, displayQuestionnaireProgressBar: true },
      props: true,
    },
    {
      path: RouterPath.UploadSuppliersForQuestionnaire,
      name: "upload-suppliers-questionnaire",
      component: UploadSuppliersQuestionnaireView,
      meta: { disableBackButton: true, displayQuestionnaireProgressBar: true },
      props: true,
    },
    {
      path: RouterPath.ForwardQuestionnaire,
      name: "forward-questionnaire",
      component: ForwardQuestionnaireView,
      meta: { disableBackButton: true, displayQuestionnaireProgressBar: true },
      props: true,
    },
    {
      path: RouterPath.SendQuestionnaire,
      name: "send-questionnaire",
      component: SendQuestionnaireView,
    },
    {
      path: RouterPath.Questionnaires,
      name: "questionnaires",
      component: QuestionnairesView,
      props: true,
    },
    {
      path: "/design",
      name: "design",
      component: DesignView,
      meta: { open: true },
    },
    {
      path: RouterPath.Team,
      name: "team",
      component: TeamView,
    },
    {
      path: RouterPath.QuestionnaireAnswers,
      name: "questionnaireAnswers",
      component: QuestionnaireAnswersView,
    },
    {
      path: RouterPath.QuestionnaireTemplate,
      name: RouterPath.QuestionnaireTemplate,
      component: QuestionnaireTemplateView,
    },
    {
      path: RouterPath.SuppliersRegistry,
      name: RouterPath.SuppliersRegistry,
      component: SupplierRegistryView,
    },
    {
      path: RouterPath.CustomerTermsAndConditions,
      name: "customerTermsAndConditions",
      component: CustomerTermsAndConditions,
      meta: { requiresTermsAndConditionAccess: true },
    },
    {
      path: RouterPath.Dashboard,
      name: "dashboard",
      component: DashboardView,
      meta: { requiresDashboardAccess: true, disableBackButton: true },
    },
    {
      path: RouterPath.DetailedRiskCategory,
      name: RouterPath.DetailedRiskCategory,
      component: DetailedRiskPageView,
      meta: { requiresDetailedRiskPageAccess: true },
    },
    {
      path: RouterPath.CreateQuestionnaire,
      name: "createQuestionnaire",
      component: CreateQuestionnaireView,
      meta: { requiresCreateQuestionnaireAccess: true },
    },
    {
      path: RouterPath.EditQuestionnaire,
      name: "editQuestionnaire",
      component: EditQuestionnaireView,
      meta: { requiresEditQuestionnaireAccess: true },
    },
    {
      path: RouterPath.UnitProfile,
      name: RouterPath.UnitProfile,
      component: UnitProfileView,
      meta: { requiresUnitProfileAccess: true },
    },
    {
      path: RouterPath.ManageUserProfile,
      name: "manageUserProfile",
      component: ManagerUserProfileView,
    },
    {
      path: RouterPath.LoginHelp,
      name: "loginHelp",
      component: LoginHelpView,
      meta: { open: true },
    },
    {
      path: RouterPath.ReceivedQuestionnaireStatus,
      name: "receivedQuestionnaireStatus",
      component: ReceivedQuestionnaireStatus,
    },
    {
      path: RouterPath.MyQuestionnaireStatus,
      name: "myQuestionnaireStatus",
      component: MyQuestionnaireStatus,
    },
    {
      path: RouterPath.UploadSuppliers,
      name: "uploadSuppliers",
      component: UploadSuppliersView,
    },
    {
      path: RouterPath.ReuseAnswers,
      name: "reuseAnswers",
      component: ReuseAnswersView,
    },
    ...adminRoutes,
    ...superUserRoutes,
    ...registerNewSupplierRoutes,
    ...helpRoutes,
  ],
});

router.beforeEach((to) => {
  const authenticationStore = useAuthenticationStore();
  const routerHelperStore = useRouterHelper();

  routerHelperStore.displayBackButton = !to.meta.disableBackButton;
  routerHelperStore.showQuestionnaireProgressBar =
    !!to.meta.displayQuestionnaireProgressBar;

  if (
    routerHelperStore.isFirstRequest &&
    to.name !== "design" &&
    to.name !== "searchCompany"
  ) {
    routerHelperStore.changeStateAfterFirstRequest();
    if (!authenticationStore.authenticated) {
      if (
        RouterPath.Login === to.name ||
        RouterPath.LoggedOut === to.name ||
        RouterPath.LoginTokenExpired === to.name ||
        RouterPath.VerifyLink === to.name
      ) {
        return;
      }
      return RouterPath.Login;
    } else {
      if (authenticationStore.isSuperUser) {
        return RouterPath.SuperUserWelcome;
      }

      return RouterPath.Home;
    }
  }

  if (!authenticationStore.authenticated) {
    if (!to.meta.open) {
      return RouterPath.Login;
    }
  } else {
    if (to.path == RouterPath.Login) {
      return RouterPath.Home;
    }

    if (
      !authenticationStore.registered &&
      !allRegisterSupplierRoutes.includes(to.path)
    ) {
      return RouterPath.RegisterSupplierWelcome;
    }

    if (
      to.meta.requiresDashboardAccess &&
      !authenticationStore.canAccessCustomerDashboard()
    ) {
      return RouterPath.Home;
    }

    if (
      to.meta.requiresCreateQuestionnaireAccess &&
      !authenticationStore.canCreateQuestionnaires()
    ) {
      return RouterPath.Home;
    }

    if (
      to.meta.requiresEditQuestionnaireAccess &&
      !authenticationStore.canEditQuestionnaires()
    ) {
      return RouterPath.Home;
    }

    if (
      to.meta.requiresDetailedRiskPageAccess &&
      !authenticationStore.canAccessDetailedRiskPage()
    ) {
      return RouterPath.Home;
    }

    if (
      to.meta.requiresUnitProfileAccess &&
      !authenticationStore.canAccessUnitProfile()
    ) {
      return RouterPath.Home;
    }

    if (
      to.meta.requiresTermsAndConditionAccess &&
      !authenticationStore.canAccessCustomerTermsAndConditions
    ) {
      return RouterPath.Home;
    }

    if (to.meta.requiresAdmin && !authenticationStore.isAdmin) {
      return RouterPath.Home;
    }

    if (to.meta.requiresSuperUser && !authenticationStore.isSuperUser) {
      return RouterPath.Home;
    }
  }
});

export default router;
